import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { ToolPage } from "../components/tools/ToolPage";
import { siteLangOf } from "../lib/marketing/registry";
import { documentHeaders } from "../lib/seo";
import { loadToolLinks } from "../lib/toolLinks";
import { toolHead } from "../lib/tools/head";
import { loadToolCopy, toolBySlug, toolPath } from "../lib/tools/index";
import { TOOLS_UI } from "../lib/tools/ui";

/**
 * One free tool, in a non-English language, at `/<lang>/tools/<slug>`.
 *
 * Two redirects rather than two live URLs, for the same reason `$lang.index`
 * has them: `siteLangOf` is forgiving — it lowercases and takes the primary
 * subtag — so `/TR/tools/jawline` and `/tr-TR/tools/jawline` both resolve to a
 * real language. Served with a 200 they would be duplicate URLs of the
 * canonical page, one per case permutation anyone links. And `/en/tools/...`
 * redirects to `/tools/...` because English lives at the bare path.
 */
export const Route = createFileRoute("/$lang/tools/$slug")({
  loader: async ({ params }) => {
    const lang = siteLangOf(params.lang);
    if (!lang) throw notFound();
    const tool = toolBySlug(params.slug);
    if (!tool) throw notFound();

    const path = toolPath(tool.slug, lang);
    if (path !== `/${params.lang}/tools/${params.slug}`)
      throw redirect({ href: path, statusCode: 301 });

    const [copy, links] = await Promise.all([
      loadToolCopy(lang),
      loadToolLinks(tool.slug, lang),
    ]);
    return { lang, tool, copy, links };
  },
  headers: documentHeaders,
  head: ({ loaderData }) =>
    loaderData
      ? toolHead(
          loaderData.tool.slug,
          loaderData.lang,
          loaderData.copy[loaderData.tool.slug],
          TOOLS_UI[loaderData.lang],
        )
      : {},
  component: LocalizedTool,
});

function LocalizedTool() {
  const { lang, tool, copy, links } = Route.useLoaderData();
  return (
    <ToolPage
      tool={tool}
      copy={copy[tool.slug]}
      ui={TOOLS_UI[lang]}
      lang={lang}
      allCopy={copy}
      links={links}
    />
  );
}
